import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { CheckCircle, XCircle, Mail, ArrowRight } from 'lucide-react'
import { authAPI } from '../services/api'

const VerifyEmail = () => {
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'success' | 'error'>('pending')
  const [errorMessage, setErrorMessage] = useState('')
  const [email, setEmail] = useState('')
  const [isResending, setIsResending] = useState(false)

  const token = searchParams.get('token')

  useEffect(() => {
    if (token) {
      verifyEmail(token)
    } else {
      setVerificationStatus('error')
      setErrorMessage('No verification token found')
    }
  }, [token])

  const verifyEmail = async (verificationToken: string) => {
    setIsLoading(true)
    try {
      const response = await authAPI.verifyEmail(verificationToken)
      setVerificationStatus('success')
      toast.success(response.data.message)
    } catch (error: any) {
      setVerificationStatus('error')
      setErrorMessage(error.response?.data?.message || 'Verification failed')
      toast.error(error.response?.data?.message || 'Verification failed')
    } finally {
      setIsLoading(false)
    }
  }

  const resendVerification = async () => {
    if (!email) {
      toast.error('Please enter your email address')
      return
    }

    setIsResending(true)
    try {
      const response = await authAPI.resendVerification(email)
      toast.success(response.data.message)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to resend verification email')
    } finally {
      setIsResending(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <h2 className="mt-4 text-xl font-semibold text-gray-900">Verifying your email...</h2>
            <p className="mt-2 text-sm text-gray-600">Please wait while we verify your email address.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {verificationStatus === 'success' ? (
            <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
          ) : verificationStatus === 'error' ? (
            <XCircle className="mx-auto h-16 w-16 text-red-500" />
          ) : (
            <Mail className="mx-auto h-16 w-16 text-gray-400" />
          )}
          
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {verificationStatus === 'success' ? 'Email Verified!' : 
             verificationStatus === 'error' ? 'Verification Failed' : 
             'Email Verification'}
          </h2>
          
          <p className="mt-2 text-sm text-gray-600">
            {verificationStatus === 'success' ? 
              'Your email has been successfully verified. You can now log in to your account.' :
              verificationStatus === 'error' ? 
              errorMessage || 'There was an error verifying your email address.' :
              'Please check your email and click the verification link.'
            }
          </p>
        </div>

        {verificationStatus === 'success' && (
          <div className="space-y-4">
            <Link
              to="/login"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Continue to Login
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        )}

        {verificationStatus === 'error' && (
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                Didn't receive the verification email? Enter your email below to resend it.
              </p>
              
              <div className="space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                
                <button
                  onClick={resendVerification}
                  disabled={isResending}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                >
                  {isResending ? 'Sending...' : 'Resend Verification Email'}
                </button>
              </div>
            </div>
            
            <div className="text-center">
              <Link
                to="/login"
                className="font-medium text-primary hover:text-primary-dark"
              >
                Back to Login
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default VerifyEmail 